import { Row } from 'read-excel-file';
import { translate } from '@jsverse/transloco';
import {
  ComplianceExcelColumnSettings,
  ComplianceFrequency,
  ComplianceImport,
  ComplianceStandard,
  ReviewComplianceCategory,
  ReviewComplianceError,
  ReviewComplianceItem,
  RoutineType
} from '../models/compliances.model';
import { ComplianceRegion } from '../../compliance-overview/models/compliance-item.model';
import { ExcelTemplateColumn } from '../../../_shared/models/excel-review.model';
import {
  containsIgnoreCase,
  downloadExcelTemplateAsync,
  findClosestName,
  splitCellList,
  toCellString
} from '../../../_shared/helpers/excel-upload.helper';

export const COMPLIANCE_EXCEL_TEMPLATE_COLUMNS: ExcelTemplateColumn[] = [
  { title: 'Compliance Standard Name' },
  { title: 'Region/s' },
  { title: 'Compliance Type', subtitle: 'Periodic or Routine' },
  { title: 'Frequency / Routine Type' },
  { title: 'Category Type', subtitle: 'Document or Inspection' },
  { title: 'Category Name' },
  { title: 'Requirements', subtitle: 'separate with commas' }
];

export interface ComplianceTemplateLookups {
  regions: string[];
  frequencies: string[];
  routineTypes: string[];
}

export function downloadComplianceExcelTemplateAsync(lookups: ComplianceTemplateLookups): Promise<void> {
  return downloadExcelTemplateAsync({
    fileName: 'compliance-upload-template.xlsx',
    sheetName: 'Compliance Template',
    columns: COMPLIANCE_EXCEL_TEMPLATE_COLUMNS,
    namedLists: [
      { name: 'Regions', values: lookups.regions },
      { name: 'Periodic', values: lookups.frequencies },
      { name: 'Routine', values: lookups.routineTypes }
    ],
    validations: [
      {
        column: 2,
        validation: {
          type: 'list',
          allowBlank: true,
          showErrorMessage: false,
          showInputMessage: true,
          promptTitle: 'Region/s',
          prompt: 'Pick a region from the dropdown, or type several separated by commas.',
          formulae: ['Regions']
        }
      },
      {
        column: 3,
        validation: {
          type: 'list',
          allowBlank: true,
          showErrorMessage: true,
          errorTitle: 'Invalid compliance type',
          error: 'Choose Periodic or Routine.',
          showInputMessage: true,
          promptTitle: 'Compliance Type',
          prompt: 'Pick Periodic or Routine from the dropdown.',
          formulae: ['"Periodic,Routine"']
        }
      },
      {
        column: 4,
        validation: {
          type: 'list',
          allowBlank: true,
          showErrorMessage: false,
          showInputMessage: true,
          promptTitle: 'Frequency/Routine Type',
          prompt: 'Pick the Compliance Type first — this dropdown follows it.',
          formulae: ['INDIRECT($C2)']
        }
      },
      {
        column: 5,
        validation: {
          type: 'list',
          allowBlank: true,
          showErrorMessage: true,
          errorTitle: 'Invalid category type',
          error: 'Choose Document or Inspection.',
          showInputMessage: true,
          promptTitle: 'Category Type',
          prompt: 'Pick Document or Inspection from the dropdown.',
          formulae: ['"Document,Inspection"']
        }
      }
    ],
    highlightedColumns: [2, 3, 4, 5]
  });
}

export const DEFAULT_COMPLIANCE_EXCEL_COLUMN_SETTINGS: ComplianceExcelColumnSettings = {
  complianceStandardNameColumn: 1,
  regionsColumn: 2,
  complianceTypeColumn: 3,
  frequencyColumn: 4,
  checklistTypeColumn: 5,
  complianceCategoryColumn: 6,
  requirementsColumn: 7
};

export function convertRowToComplianceImport(rows: Row[], userName: string, createdAt: Date, columns: ComplianceExcelColumnSettings): ComplianceImport[] {
  return rows.map(row => ({
    complianceStandardName: toCellString(row[columns.complianceStandardNameColumn - 1]),
    regions: toCellString(row[columns.regionsColumn - 1]),
    complianceType: toCellString(row[columns.complianceTypeColumn - 1]),
    frequency: toCellString(row[columns.frequencyColumn - 1]),
    complianceCategory: toCellString(row[columns.complianceCategoryColumn - 1]),
    checklistType: toCellString(row[columns.checklistTypeColumn - 1]),
    requirements: toCellString(row[columns.requirementsColumn - 1]),
    createdBy: userName,
    createdAt
  }));
}

export function stripTemplateRows(imports: ComplianceImport[]): ComplianceImport[] {
  if (imports.length > 0 && !isComplianceTypeValue(imports[0].complianceType)) {
    return imports.slice(1);
  }
  return imports;
}

function isComplianceTypeValue(value: string): boolean {
  const type = value.trim().toLowerCase();
  return type === 'periodic' || type === 'routine';
}

export function groupComplianceImports(imports: ComplianceImport[]): ReviewComplianceItem[] {
  const items = new Map<string, ReviewComplianceItem>();
  let carriedName = '';

  for (const row of imports) {
    if (isEmptyImport(row)) {
      continue;
    }

    const name = row.complianceStandardName.trim() || carriedName;
    carriedName = name;
    let item = items.get(name.toLowerCase());
    if (!item) {
      item = {
        name,
        regionNames: [],
        complianceType: row.complianceType.trim(),
        scheduleName: row.frequency.trim(),
        categories: [],
        errors: []
      };
      items.set(name.toLowerCase(), item);
    }

    for (const regionName of splitCellList(row.regions)) {
      if (!containsIgnoreCase(item.regionNames, regionName)) {
        item.regionNames.push(regionName);
      }
    }

    addCategory(item, row);
  }

  return [...items.values()];
}

function addCategory(item: ReviewComplianceItem, row: ComplianceImport): void {
  const name = row.complianceCategory.trim();
  const checklistType = row.checklistType.trim();

  let category = item.categories.find(c =>
    c.name.toLowerCase() === name.toLowerCase() && c.checklistType.toLowerCase() === checklistType.toLowerCase());
  if (!category) {
    category = { name, checklistType, requirements: [] } as ReviewComplianceCategory;
    item.categories.push(category);
  }

  for (const requirement of splitCellList(row.requirements)) {
    if (!containsIgnoreCase(category.requirements, requirement)) {
      category.requirements.push(requirement);
    }
  }
}

function isEmptyImport(row: ComplianceImport): boolean {
  return !row.complianceStandardName.trim()
    && !row.regions.trim()
    && !row.complianceType.trim()
    && !row.frequency.trim()
    && !row.complianceCategory.trim()
    && !row.checklistType.trim()
    && !row.requirements.trim();
}

export function validateReviewItem(
  item: ReviewComplianceItem,
  regions: ComplianceRegion[],
  frequencies: ComplianceFrequency[],
  routineTypes: RoutineType[],
  existingStandards: ComplianceStandard[]
): ReviewComplianceError[] {
  const errors: ReviewComplianceError[] = [];
  const missing = (message: string) => errors.push({ message, kind: 'missing' });
  const incorrect = (message: string, suggestion?: string | null) =>
    errors.push({
      message: suggestion
        ? translate('compliance.library.uploadValidation.didYouMean', { message, suggestion })
        : message,
      kind: 'incorrect'
    });

  if (!item.name) {
    missing(translate('compliance.library.uploadValidation.nameMissing'));
  } else if (existingStandards.some(standard => standard.name.toLowerCase() === item.name.toLowerCase())) {
    incorrect(translate('compliance.library.uploadValidation.nameExists', { name: item.name }));
  }

  const rawType = item.complianceType.trim();
  const type = rawType.toLowerCase();
  const schedule = item.scheduleName.trim();
  const isKnownFrequency = frequencies.some(f => f.name.toLowerCase() === schedule.toLowerCase());
  const isKnownRoutineType = routineTypes.some(r => r.name.toLowerCase() === schedule.toLowerCase());
  if (!rawType) {
    missing(translate('compliance.library.uploadValidation.typeAndScheduleMissing'));
  } else if (type !== 'periodic' && type !== 'routine') {
    incorrect(translate('compliance.library.uploadValidation.invalidType', { type: rawType }), findClosestName(rawType, ['Periodic', 'Routine']));
  } else if (type === 'periodic') {
    if (!schedule) missing(translate('compliance.library.uploadValidation.frequencyMissing'));
    else if (isKnownRoutineType && !isKnownFrequency) incorrect(translate('compliance.library.uploadValidation.routineTypeUsedForPeriodic', { schedule }));
    else if (!isKnownFrequency) incorrect(translate('compliance.library.uploadValidation.unknownFrequency', { schedule }), findClosestName(schedule, frequencies.map(f => f.name)));
  } else {
    if (!schedule) missing(translate('compliance.library.uploadValidation.routineTypeMissing'));
    else if (isKnownFrequency && !isKnownRoutineType) incorrect(translate('compliance.library.uploadValidation.frequencyUsedForRoutine', { schedule }));
    else if (!isKnownRoutineType) incorrect(translate('compliance.library.uploadValidation.unknownRoutineType', { schedule }), findClosestName(schedule, routineTypes.map(r => r.name)));
  }

  for (const regionName of item.regionNames) {
    if (!regions.some(region => region.name.toLowerCase() === regionName.toLowerCase())) {
      incorrect(translate('compliance.library.uploadValidation.unknownRegion', { name: regionName }), findClosestName(regionName, regions.map(r => r.name)));
    }
  }

  if (item.categories.length === 0) {
    missing(translate('compliance.library.uploadValidation.categoryRequired'));
  }

  for (const category of item.categories) {
    if (!category.name) {
      missing(translate('compliance.library.uploadValidation.categoryNameMissing'));
    }

    const checklistType = category.checklistType.trim().toLowerCase();
    if (checklistType !== 'document' && checklistType !== 'inspection') {
      incorrect(category.name
        ? translate('compliance.library.uploadValidation.invalidChecklistTypeForCategory', { type: category.checklistType, category: category.name })
        : translate('compliance.library.uploadValidation.invalidChecklistType', { type: category.checklistType }));
    }
  }

  return errors;
}
